

























import { Vue, Component } from "vue-property-decorator";

import TheAdminPanel from "@share/participants-import/components/the-admin-panel/TheAdminPanel.vue";
import { RouteNamesEnum } from "@service/router/RouteNamesEnum";

/**
 * //STEPS:
 */
import Step1Upload from "./components/steps/step-1-upload/Step1Upload.vue";
import Step2Preview from "./components/steps/step-2-preview/Step2Preview.vue";
import Step3Import from "./components/steps/step-3-import/Step3Import.vue";
import Step4Result from "./components/steps/step-4-result/Step4Result.vue";
import { XLSXFileService } from "@share/common/services/xlsx/XLSXFileService/XLSXFileService";
import { ParticipantsImportService } from "@share/participants-import/helpers/ParticipantsImportService";
import { UserCreateEditService } from "@share/common/services/userCreateEdit/UserCreateEditService";
import { defaultProfileFields, ProfileField } from "../../types/ProfileField";
import { RepositoryFabric } from "@api/repositories/RepositoryFabric";

import { AppliedDataCount, DuplicateAction, ImportRowData, Progress, ProgressState, ResultOfApplyingData, StepView, UserProfileDuplicate } from "@share/participants-import/types";
import { UserProfile } from "@api/models/UserProfile";
import { sleep } from "@share/web-conferences/helpers/SleepHelper";
import { mapObjectKeys } from "@common/ObjectsHelper";
import { UserProfileInterface } from "@api/models/interfaces/UserProfileInterface";

const stepsComponents = {
    Step1Upload,
    Step2Preview,
    Step3Import,
    Step4Result
}

@Component({ components: { TheAdminPanel, ...stepsComponents } })
export default class ParticipantsImport extends Vue {

    readonly TABLE_LIMIT = 1000;

    errorMessage = "";

    StepView = StepView;
    currentStep: StepView = StepView.upload;
    XLSXFileService: XLSXFileService | null = new XLSXFileService();
    importService: ParticipantsImportService | null = null;
    dataFromFile: ImportRowData[] | null = null;
    resultProfiles: UserProfile[] | null = null;

    resultCount: AppliedDataCount | null = null;

    duplicates: UserProfileDuplicate[] | null = [];
    duplicatesFileLink = "";
    resultsFileLink = "";

    progress: Progress = {
        state: ProgressState.idle,
        count: 0,
        total: this.dataFromFile?.length,
        reset: async () => {
            this.progress.state = ProgressState.empty;
            this.progress.count = 0;
            this.progress.total = 0;
        }
    }

    mainFields = [
        "firstName",
        "lastName",
        "position",
        "company"
    ];

    created() {
        this.importService = new ParticipantsImportService(
            this.$repositories.get(RepositoryFabric.USER),
            this.$repositories.get(RepositoryFabric.EVENT_MEMBERSHIP),
            this.$repositories.get(RepositoryFabric.OBJECT),
            this.$store.state.events.selected,
            this.$store,
            new UserCreateEditService(
                this.$repositories.get(RepositoryFabric.USER),
                this.$repositories.get(RepositoryFabric.SETTING),
                this.$repositories.get(RepositoryFabric.OBJECT),
                this.$repositories.get(RepositoryFabric.EVENT_MEMBERSHIP)
            )
        );
    }

    beforDestroy() {
        this.freeMemory();
        this.resultsFileLink && window.URL.revokeObjectURL(this.resultsFileLink);
    }

    async prepareToImport() {

        //стартуем поиск дублей если есть данные из файла
        if(this.dataFromFile) {

            this.progress.total = this.dataFromFile.length;

            this.duplicates = await this.importService?.getDuplicates(
                this.dataFromFile,
                this.userProfileFields.map((field) => field.name),
                this.progress
            ) ?? [];

            this.progress.reset();

            if(this.duplicates.length) {

                this.progress.state = ProgressState.idle;

                const duplicateditemsForFile = this.dataFromFile.filter((item) => this.duplicatedRows.has(item.__rowNum__.toString()));
                this.duplicatesFileLink = this.XLSXFileService?.getFileFromData("duplicates", duplicateditemsForFile as ImportRowData[]) || "";
            }
            else {
                //если дупликатов нет то выполняем применение даных
                this.nextStep();
            }
        }
    }

    /**
     * Применение данных импорта
     */
    async applyData() {

        if(!this.importService) {
            throw new Error(`Type error. The importService is ${this.importService}`);
        }

        await sleep(100);

        this.progress.state = ProgressState.prepare;

        let rowsForCreate: ImportRowData[] | null = this.rowsForCreate;
        let rowsForUpdate: ImportRowData[] | null = this.rowsForupdate;

        if (!rowsForCreate.length && !rowsForUpdate.length) {
            return
        }

        try {

            this.progress.total = 100;

            this.progress.state = ProgressState.import;

            let appliedData: ResultOfApplyingData | null = (await this.importService.applyImport(this.duplicates || [], rowsForCreate, this.progress));

            this.resultCount = {
                created: appliedData.created.length,
                updated: appliedData.updated.length
            }
            this.resultProfiles = this.getResultData(appliedData);

            rowsForCreate = null;
            rowsForUpdate = null;

            let resultDataForFile: UserProfileInterface[] | null = this.getResultDataForFile([...appliedData.created, ...appliedData.updated]);

            appliedData = null;

            //create file
            this.resultsFileLink = this.XLSXFileService?.getFileFromData("result", resultDataForFile) || "";
            resultDataForFile = null;

        }
        catch(e) {
            this.errorMessage = this.$t("participants.import.error").toString();
            this.$store.dispatch("reportError", e);
        }
        finally {
            //FINISH - освободить память
            this.freeMemory();
            await this.finishApplyProgress();
        }
    }

    freeMemory() {
        this.dataFromFile = null;
        this.duplicates = null;

        this.importService && (this.importService.userCodes = undefined);
        this.duplicatesFileLink && window.URL.revokeObjectURL(this.duplicatesFileLink);
    }

    get rowsForCreate() {
        return this.duplicates ? this.getRowsForCreate(this.dataFromFile || []) : this.dataFromFile || [];
    }

    get rowsForupdate() {
        return this.duplicates
            ?.filter((item) => item.action?.id === DuplicateAction.update && item.source)
            .map(item => item.source as ImportRowData) || [];
    }

    async finishApplyProgress() {
        this.progress.reset();
        this.progress.state = ProgressState.idle;
    }

    getRowsForCreate(dataFromFile: ImportRowData[]) {
        //не дублированные данные из файла
        const notDuplicated = dataFromFile.filter((item) => !this.duplicatedRows.has(item.__rowNum__.toString())) || [];

        //дубликаты выбранные для добавления
        const duplicatesForAdd = this.duplicates
            ?.filter((item) => item.action?.id === DuplicateAction.add && item.source)
            .map((item) => item.source || {});

        // строки для создания из них полльзователей и профилей
        return notDuplicated.concat(duplicatesForAdd || []);
    }

    getResultData(data: ResultOfApplyingData) {

        let fields: string[] = this.resultTableFields;

        return [...data.created, ...data.updated].slice(0, this.TABLE_LIMIT).map((item) => {

            const result: any = {};

            fields.forEach((fieldName) => result[fieldName] = item[fieldName])

            return new UserProfile(result);
        })
    }

    getResultDataForFile(data: Record<string, string>[]) {
        return data.map(item => mapObjectKeys(item, this.resultTableFields));
    }

    get greenButtonDisabled() {
        return (!this.dataFromFile && this.currentStep !== StepView.result)
            || (this.currentStep === StepView.import && !this.rowsForCreate.length && !this.rowsForupdate.length)
            || this.progress.state !== ProgressState.idle
            || !!this.errorMessage
    }

    get backButtonDisabled() {
        return this.progress.state !== ProgressState.idle
            || this.currentStep === StepView.upload
            || this.currentStep === StepView.result
            || !!this.errorMessage
    }

    get resultAdditionalFields() {

        const profileFieldNames = this.userProfileFields
                ?.filter((field: ProfileField) => !this.mainFields.includes(field.name))
                .slice(0, 4) || [];

        return profileFieldNames;
    }

    get resultTableFields() {
        return [...this.mainFields, ...this.resultAdditionalFields.map((field: ProfileField) => field.name), "code"];
    }

    fileUploadHandler(data: Record<string, string>[]) {
        this.dataFromFile = data;
        this.currentStep = StepView.preview;
    }

    invalidFileHandler() {
        if(this.dataFromFile) {
            this.dataFromFile = null;
        }
    }

    startOver() {
        this.errorMessage = "";
        this.freeMemory();
        this.currentStep = StepView.upload;
    }

    /**
     * step up
     */
    async nextStep() {

        if(this.currentStep === StepView.preview) {
            this.prepareToImport();
        }

        if(this.currentStep === StepView.import && this.dataFromFile?.length) {
            await this.applyData();
        }

        // кнопка готово
        if(this.currentStep <= 3 && !this.errorMessage) {
            this.currentStep++;
        }
    }

    /**
     * step back
     */
    back() {
        if(this.backButtonDisabled) {
            return;
        }

        if(this.currentStep > 1) {
            this.currentStep--;
        }
    }

    // make redirect to participants table page
    onReady() {
        this.$router.push({
            name: RouteNamesEnum.participants,
            params: {
                eventId: this.$route.params.eventId,
                backend: this.$route.params.backend
            }
        })
    }

    getLimit(items: any[] | null) {
        return (items?.length ?? 0) > this.TABLE_LIMIT ? this.TABLE_LIMIT : undefined
    }

    get duplicatedRows() {
        return new Set(this.duplicates?.map((duplicate) => duplicate.__rowNum__));
    }

    get contentBackgroundBlured() {
        return [StepView.upload].includes(this.currentStep) || this.onProgress
    }

    get step1Props() {
        return {
            XLSXFileService: this.XLSXFileService,
            userProfileFields: this.userProfileFields
        }
    }

    get step2Props() {
        return {
            previewItems: this.dataFromFile?.slice(0, this.TABLE_LIMIT),
            limit: this.getLimit(this.dataFromFile),
            total: this.dataFromFile?.length ?? 0
        }
    }

    get step3Props() {
        return {
            progress: this.progress,
            rows: this.duplicates,
            fileLink: this.duplicatesFileLink,
            limit: this.getLimit(this.duplicates)
        }
    }

    get step4Props() {
        const total = (this.resultCount?.created ?? 0) + (this.resultCount?.updated ?? 0);

        return {
            profiles: this.resultProfiles,
            resultCount: this.resultCount,
            resultAdditionalFields: this.resultAdditionalFields,
            fileLink: this.resultsFileLink,
            limit: total > this.TABLE_LIMIT ? this.TABLE_LIMIT : undefined
        }
    }

    get userProfileFields() {
        const profileParams = this.$store.getters['events/selectedEventViewParam']('profile', null);
        let fields: ProfileField[] = []

        if (profileParams?.fields) {
            fields = profileParams.fields as ProfileField[];
        }
        else {
            fields = defaultProfileFields;
        }

        return fields.filter(field => field.type !== "tag");
    }

    get onProgress() {
        return this.progress.state !== ProgressState.idle
    }
}

